import { ApolloError } from '@apollo/client';

import { useApiGatewayQuery } from '~hooks';
import { PRODUCT, PRODUCT_BY_SLUG } from '~queries';
import {
  GetProductBySlugQuery,
  GetProductBySlugQueryVariables,
  GetProductsQuery,
  GetProductsQueryVariables,
  Product,
} from '~types/appsync.types';

interface UseProductsOptions {
  productSlug?: string;
  productCategoryShortname?: string;
}

const useProducts = (
  options: UseProductsOptions = {},
): [Product[] | undefined, boolean, ApolloError | undefined] => {
  const { productSlug, productCategoryShortname } = options;

  // Validate productSlug is a non-empty string before making the query
  const hasValidSlug = !!productSlug?.trim();

  const bySlugResult = useApiGatewayQuery<GetProductBySlugQuery, GetProductBySlugQueryVariables>(
    PRODUCT_BY_SLUG,
    {
      skip: !hasValidSlug,
      variables: { productSlug: productSlug! },
    },
  );

  const byCategoryResult = useApiGatewayQuery<GetProductsQuery, GetProductsQueryVariables>(
    PRODUCT,
    {
      skip: hasValidSlug || !productCategoryShortname,
      variables: { productCategoryShortname: productCategoryShortname! },
    },
  );

  if (hasValidSlug) {
    const product = bySlugResult.data?.productBySlug;
    return [
      product ? ([product] as Product[]) : undefined,
      bySlugResult.loading,
      bySlugResult.error,
    ];
  }

  return [
    byCategoryResult.data?.products as Product[] | undefined,
    byCategoryResult.loading,
    byCategoryResult.error,
  ];
};

export default useProducts;
